/**
 * Durable appraisal email intents.
 *
 * Previously the "send the email once every photo has uploaded" wait lived in
 * an in-memory promise inside StepBasedAppraisal. If the driver closed the app
 * (or uploads finished later, as they always do offline) the wait died with the
 * JS context and the email was never attempted.
 *
 * An intent is persisted to localStorage at completion time and drained in the
 * background: once the appraisal has zero pending photo uploads, the existing
 * send path runs. No UI, no buttons, no user action.
 */

import { getPendingCountForAppraisal } from "@/lib/appraisalUploadQueue";
import { triggerAppraisalEmailSend, type AppraisalType } from "@/lib/appraisalEmail";
import { captureError, captureMetric } from "@/lib/observability";
import { isKnownOffline } from "./connectivity";

const STORAGE_KEY = "tm_appraisal_email_intents";
const POLL_MS = 30_000;

export interface AppraisalEmailIntent {
  appraisalId: string;
  jobId: string;
  appraisalType?: AppraisalType;
  customerEmail: string;
  ccEmail?: string;
  source?: string;
  createdAt: number;
  attempts: number;
}

let intervalId: ReturnType<typeof setInterval> | null = null;
let cleanup: (() => void) | null = null;
let draining = false;

function read(): AppraisalEmailIntent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(intents: AppraisalEmailIntent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(intents));
  } catch { /* storage full — the in-flight attempt still runs */ }
}

/**
 * Mirror the queue state onto the appraisal row so the job page shows the real
 * state ("Queued", with the reason) instead of a permanent "Not Sent".
 * Never overwrites a terminal `sent`, and never blocks the queue.
 */
export async function markAppraisalEmailQueued(
  appraisalId: string,
  recipient: string | null,
  reason: string,
): Promise<void> {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase
      .from("vehicle_appraisals")
      .update({ email_status: "pending", email_error: reason, email_recipient: recipient })
      .eq("id", appraisalId)
      .neq("email_status", "sent");
  } catch { /* status mirroring must never break the send pipeline */ }
}

/**
 * No recipient, or automatic appraisal email disabled for this customer:
 * record it so the job page states that plainly and still offers a manual send.
 */
export async function markAppraisalEmailNotRequired(appraisalId: string): Promise<void> {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase
      .from("vehicle_appraisals")
      .update({
        email_status: "skipped",
        email_error: "Automatic sending is off for this customer, or no email address is on file.",
      })
      .eq("id", appraisalId)
      .is("email_sent_at", null)
      .neq("email_status", "sent");
  } catch { /* status mirroring must never break the pipeline */ }
}


/** Persist an email intent and attempt it immediately. */
export function queueAppraisalEmail(
  intent: Omit<AppraisalEmailIntent, "createdAt" | "attempts">,
): void {
  const existing = read().filter((i) => i.appraisalId !== intent.appraisalId);
  existing.push({ ...intent, createdAt: Date.now(), attempts: 0 });
  write(existing);
  captureMetric("appraisal.email", "queued", 0, { appraisalId: intent.appraisalId });
  void markAppraisalEmailQueued(
    intent.appraisalId,
    intent.customerEmail ?? null,
    "Queued — sends automatically once every photo has uploaded.",
  );
  void processAppraisalEmailQueue();
}

export function getPendingAppraisalEmailCount(): number {
  return read().length;
}

/** Drain any intents whose photos have all finished uploading. */
export async function processAppraisalEmailQueue(): Promise<void> {
  if (draining) return;
  if (isKnownOffline()) return;

  draining = true;
  try {
    const intents = read();
    if (intents.length === 0) return;

    const remaining: AppraisalEmailIntent[] = [];
    for (const intent of intents) {
      const pendingPhotos = await getPendingCountForAppraisal(intent.appraisalId).catch(() => 0);
      if (pendingPhotos > 0) {
        captureMetric("appraisal.email", "held_for_uploads", pendingPhotos, {
          appraisalId: intent.appraisalId, pendingCount: pendingPhotos,
        });
        void markAppraisalEmailQueued(
          intent.appraisalId,
          intent.customerEmail ?? null,
          `Queued — waiting for ${pendingPhotos} photo${pendingPhotos === 1 ? "" : "s"} to finish uploading.`,
        );
        remaining.push(intent);
        continue;
      }

      try {
        captureMetric("appraisal.email", "released_after_uploads", 0, {
          appraisalId: intent.appraisalId, pendingCount: 0,
        });
        const { error } = await triggerAppraisalEmailSend({
          appraisalId: intent.appraisalId,
          jobId: intent.jobId,
          appraisalType: intent.appraisalType,
          customerEmail: intent.customerEmail,
          ccEmail: intent.ccEmail,
          force: true,
          source: intent.source || "durable_email_queue",
        });

        if (error) {
          remaining.push({ ...intent, attempts: intent.attempts + 1 });
          captureMetric("appraisal.email", "retry", 0, {
            appraisalId: intent.appraisalId, attempts: intent.attempts + 1, error,
          });
        } else {
          captureMetric("appraisal.email", "sent", Date.now() - intent.createdAt, {
            appraisalId: intent.appraisalId, attempts: intent.attempts,
          });
        }
      } catch (err) {
        remaining.push({ ...intent, attempts: intent.attempts + 1 });
        captureError("appraisal.email", err, {
          severity: "warn",
          metadata: { appraisalId: intent.appraisalId, attempts: intent.attempts + 1 },
        });
      }
    }

    write(remaining);
  } catch (err) {
    captureError("appraisal.email.drain", err);
  } finally {
    draining = false;
  }
}

export function startAppraisalEmailQueue(): void {
  if (intervalId) return;
  void processAppraisalEmailQueue();
  intervalId = setInterval(() => { void processAppraisalEmailQueue(); }, POLL_MS);

  const onOnline = () => { void processAppraisalEmailQueue(); };
  const onVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      void processAppraisalEmailQueue();
    }
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisible);
  cleanup = () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export function stopAppraisalEmailQueue(): void {
  if (intervalId) { clearInterval(intervalId); intervalId = null; }
  if (cleanup) { cleanup(); cleanup = null; }
}
